import { Children, isValidElement, useRef, useState, type HTMLAttributes, type ReactNode } from 'react';
import { createPortal } from 'react-dom';
import { usePhone, useSlot } from '@/lib/responsive';
import { Icons } from './primitives';
import { ResponsivePicker } from './responsive-picker';
import { useScrollAffordance } from './scroll-table';

/** The visible text of the child carrying `aria-selected`, for the View button. */
function selectedLabel(children: ReactNode): string {
  const active = Children.toArray(children).find(
    (child) => isValidElement<{ 'aria-selected'?: boolean }>(child) && child.props['aria-selected'] === true,
  );
  if (!isValidElement<{ children?: ReactNode }>(active)) return '';
  const text = (node: ReactNode): string =>
    Children.toArray(node)
      .map((c) => (typeof c === 'string' || typeof c === 'number' ? String(c) : isValidElement<{ children?: ReactNode }>(c) ? text(c.props.children) : ''))
      .join('');
  return text(active.props.children).trim();
}

/**
 * A row of view tabs that may be wider than a phone. Above phone width the
 * frame is `display: contents` and the markup is the tablist it replaces.
 * On a phone the tablist stays in the DOM (hidden) and a `VIEW <active> ▾`
 * button opens the same links as a sheet. When the page's phone filter bar
 * offers a slot (`ResponsiveFilters`), the button is portaled into it so
 * View and Filters share one row.
 */
export function TabStrip({ children, className, ...props }: HTMLAttributes<HTMLDivElement>) {
  const ref = useRef<HTMLDivElement>(null);
  const edge = useScrollAffordance(ref);
  const phone = usePhone();
  const [open, setOpen] = useState(false);
  const slot = useSlot('.mobile-filter-bar-slot', phone);
  const tablist = (
    <div className="tab-strip" data-scroll={edge} data-phone-picker={phone || undefined}>
      <div ref={ref} role="tablist" {...props} className={className}>{/* screens pass role="tablist" too: the tab-link guard scans for it */}
        {children}
      </div>
    </div>
  );
  if (!phone) return tablist;
  const picker = (
    <>
      <button
        type="button"
        className="btn tab-strip-view"
        aria-haspopup="dialog"
        aria-expanded={open}
        onClick={() => setOpen(true)}
      >
        <span className="tab-strip-view-label" aria-hidden="true">View</span>
        <span className="tab-strip-view-value">{selectedLabel(children) || 'Select a view'}</span>
        <span className="tab-strip-view-chev" aria-hidden="true">{Icons.chev_d}</span>
      </button>
      {open && (
        <ResponsivePicker label="View" onClose={() => setOpen(false)}>
          <div className="tab-strip-sheet" role="tablist" aria-label={props['aria-label']} onClick={(event) => { if ((event.target as HTMLElement).closest('a,button')) setOpen(false); }}>
            {children}
          </div>
        </ResponsivePicker>
      )}
    </>
  );
  return (
    <>
      {slot ? createPortal(picker, slot) : picker}
      {tablist}
    </>
  );
}
